import { useState } from 'react';
import { Input } from './ui/input';
import { useSearch } from '../hooks/useKeys';
import {
  Command,
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator
} from './ui/command';
import { Search, Loader2, Settings2, CheckSquare, Square } from 'lucide-react';
import { cn } from './ui/lib/utils';
import { Checkbox } from './ui/checkbox';
import { Label } from './ui/label';

export default function SearchBar() {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [includeValues, setIncludeValues] = useState(true);
  const [exactKeySearch, setExactKeySearch] = useState(false);
  const [searchLimit, setSearchLimit] = useState(20);
  const [showSettings, setShowSettings] = useState(false);

  const { data, isLoading, handleSelectSearchResult } = useSearch(searchQuery, {
    enabled: isOpen && searchQuery.length >= 2,
    includeValues,
    limit: searchLimit,
    exactKey: exactKeySearch
  });

  const handleInputFocus = () => {
    setIsOpen(true);
  };

  const handleCommandInput = (value: string) => {
    setSearchQuery(value);
  };

  const handleSelectItem = (result: any) => {
    handleSelectSearchResult(result);
    setIsOpen(false);
    setSearchQuery('');
  };

  const toggleSettings = () => {
    setShowSettings(!showSettings);
  };

  return (
    <div>
      <div className="relative w-[40vw] dark">
        <Search className="absolute left-2 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <Input
          type="text"
          placeholder="Search across all namespaces...Results might take a while (1-3 minutes)"
          className="w-full pl-8 pr-4"
          onFocus={handleInputFocus}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
      </div>

      <CommandDialog open={isOpen} onOpenChange={setIsOpen}>
        <Command shouldFilter={false}>
          <div className="flex items-center justify-between border-b px-3">
            <CommandInput
              placeholder="Search across all namespaces..."
              value={searchQuery}
              onValueChange={handleCommandInput}
              className="flex-1"
            />
            <button
              type="button"
              onClick={toggleSettings}
              className="ml-2 p-2 rounded-md mr-8  hover:bg-slate-100 text-slate-500"
              title="Search options"
            >
              <Settings2 className="h-4 w-4" />
            </button>
          </div>

          {showSettings && (
            <div className="p-2 border-b text-sm">
              <div className="mb-3">
                <div className="flex items-center space-x-2 mb-2">
                  <Checkbox
                    id="include-values"
                    checked={includeValues}
                    onCheckedChange={(checked) => setIncludeValues(checked as boolean)}
                  />
                  <Label htmlFor="include-values" className="text-sm cursor-pointer">
                    Search in values (slower but more thorough)
                  </Label>
                </div>

                <div className="flex items-center space-x-2 mb-2">
                  <Checkbox
                    id="exact-key"
                    checked={exactKeySearch}
                    onCheckedChange={(checked) => setExactKeySearch(checked as boolean)}
                  />
                  <Label htmlFor="exact-key" className="text-sm cursor-pointer">
                    Exact key search (find across all namespaces)
                  </Label>
                </div>
              </div>

              <div className="mb-2">
                <Label htmlFor="search-limit" className="text-sm mb-1 block">
                  Max results per namespace: {searchLimit}
                </Label>
                <div className="flex items-center gap-2">
                  <span className="text-xs">5</span>
                  <input
                    id="search-limit"
                    type="range"
                    min="5"
                    max="50"
                    value={searchLimit}
                    onChange={(e) => setSearchLimit(Number(e.target.value))}
                    className="flex-1"
                  />
                  <span className="text-xs">50</span>
                </div>
              </div>

              <p className="text-xs text-muted-foreground mt-2">
                Note: Searching with higher limits or across values may be slower due to API rate limits.
                {exactKeySearch && " Exact key search is more efficient when looking for a specific key across namespaces."}
              </p>
            </div>
          )}

          <CommandList>
            {isLoading && (
              <div className="flex items-center justify-center py-6">
                <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                <span className="ml-2 text-sm text-muted-foreground">Searching...</span>
              </div>
            )}
            <CommandEmpty>No results found. Results might take a while to search across all namespaces (1-3 minutes)</CommandEmpty>

            {data?.results && data.results.length > 0 && (
              <CommandGroup heading="Search Results">
                {data.results.map((result, index) => (
                  <CommandItem
                    key={`${result.namespaceId}_${result.key}_${index}`}
                    onSelect={() => handleSelectItem(result)}
                    className="flex items-center"
                  >
                    <div className="flex-1 truncate">
                      <div className="font-medium">{result.key}</div>
                      <div className="text-xs text-muted-foreground truncate">
                        {typeof result.value === 'object'
                          ? `${JSON.stringify(result.value).substring(0, 60)}...`
                          : `${String(result.value)?.substring(0, 60)}...`}
                      </div>
                    </div>
                    <div
                      className={cn(
                        "ml-2 rounded-full px-2 py-1 text-xs",
                        result.namespaceName === "ze_envs"
                          ? "bg-green-100 text-green-800"
                          : result.namespaceName === "ze_snapshots"
                            ? "bg-blue-100 text-blue-800"
                            : result.namespaceName === "ze_files"
                              ? "bg-purple-100 text-purple-800"
                              : "bg-gray-100 text-gray-800"
                      )}
                    >
                      {result.namespaceName}
                    </div>
                    {result.matchLocation && (
                      <div className="ml-2 text-xs text-muted-foreground">
                        {result.matchLocation === 'key'
                          ? 'Key'
                          : result.matchLocation === 'value'
                            ? 'Value'
                            : 'Both'}
                        {result.exactMatch && ' (Exact)'}
                      </div>
                    )}
                  </CommandItem>
                ))}
              </CommandGroup>
            )}
          </CommandList>
        </Command>
      </CommandDialog>
    </div>
  );
}